/**
 * @typedef {'json' | 'xml' | 'html' | 'chatbot'} OutputFormat
 */

/**
 * @typedef {Object} FAQItem
 * @property {string} question
 * @property {string} answer
 */

/**
 * @typedef {Object} FAQCategory
 * @property {FAQItem[]=} details
 * @property {FAQItem[]=} faqList
 */

/**
 * @typedef {Object} FAQChatBotOutputRendererProps
 * @property {FAQCategory[]} data
 * @property {OutputFormat} format
 * @property {string} language
 */

import React from 'react';

// Utility functions to convert FAQ data into different output formats
import { convertToHTML } from './utils/formatHTML';
import { convertToXML } from './utils/formatXML';
import { convertToJSON } from './utils/formatJSON';

// Secure renderer to safely inject HTML into the DOM (prevents XSS)
import SafeHTMLRenderer from '../../../utils/SafeHTMLRenderer';

// Converts structured FAQ data into chatbot-readable conversation text
import { convertToChatbotTextFromFAQ } from '../../../utils/formatChatbotTextFromFAQ';

// CSS module for scoped styling
import style from './KidsChatBotFAQ.module.css';

/**
 * FAQChatBotOutputRenderer
 * ------------------------------------
 * Type-safe renderer for multiple FAQ output formats.
 *
 * @param {FAQChatBotOutputRendererProps} props
 */
function FAQChatBotOutputRenderer({ data, format, language }) {
  switch (format) {
    //  Render raw JSON output (string enforced)
    case 'json':
      return (
        <pre className={style.raw_output}>
          <code>{convertToJSON(data, language)}</code>
        </pre>
      );

    //  Render raw XML output (string enforced)
    case 'xml':
      return (
        <pre className={style.raw_output}>
          <code>{convertToXML(data, language)}</code>
        </pre>
      );

    //  Render chatbot-style conversational format (string enforced)
    case 'chatbot':
      return (
        <pre className={style.raw_output}>
          <code>{convertToChatbotTextFromFAQ(data, language)}</code>
        </pre>
      );

    //  Default rendering: formatted and safely injected HTML
    case 'html':
    default:
      return <SafeHTMLRenderer content={convertToHTML(data, language)} />;
  }
}

export default FAQChatBotOutputRenderer;
